// Location Service - Multi-location support for SLIK Barber Lounge and SLEK Hair Studio
// Connects to slik-ai-manager API for cross-location conflict detection

import { isLocalApiUnavailable } from '../utils/devApi';

const API_BASE = process.env.REACT_APP_AI_API_URL || 'http://localhost:8001';

export interface Location {
  id: string;
  name: string;
  shortName: string;
  address: string;
  phone: string;
  timezone: string;
  isActive: boolean;
}

export interface ConflictCheckResult {
  hasConflict: boolean;
  barberId: string;
  requestedDate: string;
  requestedTime: string;
  requestedDuration: number;
  conflictingAppointment?: any;
  message: string;
}

export interface AvailabilitySlot {
  time: string;
  available: boolean;
  conflictLocation?: string;
}

class LocationService {
  private currentLocationId: string = 'loc-barber-lounge';
  private listeners: Set<(locationId: string) => void> = new Set();
  private locationsCache: Location[] | null = null;

  constructor() {
    // Load from localStorage
    const saved = localStorage.getItem('slikpos_current_location');
    if (saved) {
      this.currentLocationId = saved;
    }
  }

  // Fetch all locations from API
  async getAllLocations(): Promise<Location[]> {
    if (this.locationsCache) {
      return this.locationsCache;
    }

    try {
      // On the deployed site the localhost dev API is unreachable — skip it silently and use the fallback.
      if (isLocalApiUnavailable(API_BASE)) throw new Error('__skip_local_api__');
      const response = await fetch(`${API_BASE}/api/locations`);
      if (!response.ok) throw new Error('Failed to fetch locations');
      const data = await response.json();
      this.locationsCache = data.locations.map((loc: any) => ({
        id: loc.id,
        name: loc.name,
        shortName: loc.short_name,
        address: loc.address,
        phone: loc.phone,
        timezone: loc.timezone,
        isActive: loc.is_active
      }));
      return this.locationsCache!;
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Fallback to hardcoded locations
      return [
        {
          id: 'loc-barber-lounge',
          name: 'SLIK Barber Lounge',
          shortName: 'Barber Lounge',
          address: '123 Main St, Los Angeles, CA',
          phone: '(555) 123-4567',
          timezone: 'America/Los_Angeles',
          isActive: true
        },
        {
          id: 'loc-hair-studio',
          name: 'SLEK Hair Studio',
          shortName: 'Hair Studio',
          address: '456 Oak Ave, Los Angeles, CA',
          phone: '(555) 987-6543',
          timezone: 'America/Los_Angeles',
          isActive: true
        }
      ];
    }
  }

  getCurrentLocationId(): string {
    return this.currentLocationId;
  }

  async getCurrentLocation(): Promise<Location | null> {
    const locations = await this.getAllLocations();
    return locations.find(l => l.id === this.currentLocationId) || null;
  }

  setCurrentLocation(locationId: string): void {
    this.currentLocationId = locationId;
    localStorage.setItem('slikpos_current_location', locationId);
    this.notifyListeners();
  }

  subscribe(callback: (locationId: string) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(): void {
    this.listeners.forEach(cb => cb(this.currentLocationId));
  }

  /**
   * CRITICAL: Check if booking conflicts with other locations.
   * This prevents double-booking staff like Kelly who work at multiple locations.
   */
  async checkConflict(
    barberId: string,
    date: string,
    time: string,
    duration: number
  ): Promise<ConflictCheckResult> {
    try {
      const response = await fetch(`${API_BASE}/api/appointments/check-conflict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          barber_id: barberId,
          date,
          time,
          duration
        })
      });

      if (!response.ok) throw new Error('Conflict check failed');

      const data = await response.json();
      return {
        hasConflict: data.has_conflict,
        barberId: data.barber_id,
        requestedDate: data.requested_date,
        requestedTime: data.requested_time,
        requestedDuration: data.requested_duration,
        conflictingAppointment: data.conflicting_appointment,
        message: data.message
      };
    } catch (error) {
      console.error('Error checking conflict:', error);
      // On error, allow booking (fail open) but log warning
      return {
        hasConflict: false,
        barberId,
        requestedDate: date,
        requestedTime: time,
        requestedDuration: duration,
        message: 'Unable to verify availability - proceeding with booking'
      };
    }
  }

  /**
   * Get barber availability with cross-location blocking.
   * Shows which slots are blocked and at which location.
   */
  async getBarberAvailability(
    barberId: string,
    date: string,
    slotDuration: number = 30
  ): Promise<AvailabilitySlot[]> {
    try {
      const locationId = this.getCurrentLocationId();
      const response = await fetch(
        `${API_BASE}/api/barbers/${barberId}/availability?date=${date}&location_id=${locationId}&slot_duration=${slotDuration}`
      );

      if (!response.ok) throw new Error('Failed to fetch availability');

      const data = await response.json();
      return data.slots.map((slot: any) => ({
        time: slot.time,
        available: slot.available,
        conflictLocation: slot.conflict_location
      }));
    } catch (error) {
      console.error('Error fetching availability:', error);
      return [];
    }
  }

  /**
   * Get barbers who work at a specific location
   */
  async getBarbersByLocation(locationId?: string): Promise<any[]> {
    try {
      const locId = locationId || this.currentLocationId;
      const response = await fetch(`${API_BASE}/api/locations/${locId}/barbers`);
      if (!response.ok) throw new Error('Failed to fetch barbers');
      const data = await response.json();
      return data.barbers;
    } catch (error) {
      console.error('Error fetching barbers by location:', error);
      return [];
    }
  }

  /**
   * Check if a barber works at the current location
   */
  barberWorksAtCurrentLocation(barber: { locationIds?: string[] }): boolean {
    const locationIds = barber.locationIds || ['loc-barber-lounge'];
    return locationIds.includes(this.currentLocationId);
  }
}

export const locationService = new LocationService();
